import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiCheckCircle, FiCheck } = FiIcons;

const TeamReportDetail = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [doneHistory, setDoneHistory] = useState([]);

  useEffect(() => {
    if (teamId) fetchData();
  }, [teamId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 0. Get Team Info
      const teamRes = await apiService.getTeamById(teamId);
      setTeam(teamRes.data || { name: 'Unbekanntes Team' });

      // 1. Get Team Used Recs
      const teamRecsRes = await apiService.getTeamUsedRecommendations({ team_id: teamId, limit: 1000 });
      const teamRecs = teamRecsRes.data || [];

      if (teamRecs.length === 0) {
        setDoneHistory([]);
        setLoading(false);
        return;
      }

      // 2. Get Rec Details
      const allRecsRes = await apiService.getRecommendations({ limit: 1000 });
      const allRecs = allRecsRes.data || [];

      // 3. Map details
      const enrichedRecs = teamRecs.map(tr => {
        const rec = allRecs.find(r => r.id === tr.recommendation_id);
        return { ...tr, ...rec, recommendation_id: tr.recommendation_id }; 
      });

      // 4. Get History (Status Changes)
      const changesRes = await apiService.getRecommendationStatusChanges({ limit: 1000 });
      const allChanges = changesRes.data || [];

      const teamRecIds = enrichedRecs.map(r => r.recommendation_id);
      const relevantChanges = allChanges.filter(c => teamRecIds.includes(c.recommendation_id));
      
      // Enrich changes with Rec titles
      const historyData = relevantChanges.map(c => {
         const rec = allRecs.find(r => r.id === c.recommendation_id);
         return { ...c, recTitle: rec?.title };
      });

      // Sort by date desc (newest first)
      historyData.sort((a, b) => new Date(b.datetime) - new Date(a.datetime));

      // 5. Filter Done History (Only currently Implemented Recs)
      // Identify IDs of recommendations that are currently Status 2
      const currentDoneRecIds = enrichedRecs
        .filter(r => String(r.in_progress_used) === '2')
        .map(r => r.recommendation_id);

      const doneEvents = [];
      const processedDoneIds = new Set();

      // Iterate history (newest first) to find the latest 'set to 2' event for each currently done rec
      for (const event of historyData) {
        if (
             String(event.status_change_end) === '2' && 
             currentDoneRecIds.includes(event.recommendation_id) &&
             !processedDoneIds.has(event.recommendation_id)
           ) {
             doneEvents.push(event);
             processedDoneIds.add(event.recommendation_id);
        }
      }

      setDoneHistory(doneEvents);

    } catch (e) {
      console.error("Error fetching report data", e);
    } finally {
      setLoading(false);
    }
  };

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  if (loading) {
     return (
       <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
       </div>
     );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
       <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/admin', { state: { activeTab: 'reports' } })}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
             <SafeIcon icon={FiArrowLeft} className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
               Report: {team?.name}
            </h1>
            <p className="text-sm text-gray-500">Erfolgs-Timeline und umgesetzte Maßnahmen</p>
          </div>
       </div>

       <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-4">
             <div className="p-2 bg-green-100 rounded-full">
                <SafeIcon icon={FiCheckCircle} className="text-green-600 w-5 h-5" />
             </div>
             <h3 className="text-lg font-bold text-gray-800">Erfolgs-Timeline</h3>
          </div>

          {doneHistory.length === 0 ? (
             <div className="p-12 text-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 text-gray-400">
                <SafeIcon icon={FiCheckCircle} className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Dieses Team hat noch keine Maßnahmen erfolgreich umgesetzt.</p>
             </div>
          ) : (
             <div className="relative pl-8 border-l-2 border-green-200 space-y-8 ml-4">
                {doneHistory.map((item, index) => (
                   <div key={item.id || index} className="relative">
                      {/* Dot on timeline */}
                      <div className="absolute -left-[41px] top-1 h-5 w-5 rounded-full bg-green-500 border-4 border-white shadow-sm flex items-center justify-center">
                         <SafeIcon icon={FiCheck} className="text-white w-3 h-3" />
                      </div>
                      
                      {/* Content */}
                      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all">
                         <span className="text-xs font-semibold text-green-600 mb-1 block">
                            {formatDateOnly(item.datetime)}
                         </span>
                         <h4 className="font-bold text-gray-900">{item.recTitle || `Empfehlung #${item.recommendation_id}`}</h4>
                         <p className="text-xs text-gray-500 mt-1">Status geändert auf Umgesetzt</p>
                      </div>
                   </div>
                ))}
             </div>
          )}
       </div>
    </div>
  );
};

export default TeamReportDetail;